import React, { useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { FA } from "components/FA";
import { openTermsModal } from "components/Terms";
import * as Yup from "yup";

import { accountService, alertService, modalService } from "services";

function Register({ isModal = false, close = null }) {
  const history = useHistory();
  const initialValues = {
    email: "",
    password: "",
    confirmPassword: "",
    gdpr: false,
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Email is invalid").required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("You must confirm your password"),
    gdpr: Yup.bool().oneOf(
      [true],
      "You must agree to our Terms & Conditions to register"
    ),
  });

  const onSubmit = useCallback(
    (fields, { setStatus, setSubmitting }) => {
      setStatus();
      accountService
        .register(fields)
        .then(() => {
          alertService.success(
            "Registration successful, please check your email for verification instructions",
            { keepAfterRouteChange: true }
          );
          if (typeof close === "function") {
            close();
          } else {
            history.push("login");
          }
        })
        .catch((error) => {
          setSubmitting(false);
          alertService.error(error);
        });
    },
    [close, history]
  );

  const handleTerms = useCallback((e) => {
    if (false) {
      e.preventDefault();
      openTermsModal();
    }
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form>
          <h3 className="card-header">
            Register{" "}
            {isModal && (
              <FA
                title="Close"
                onClick={() => close()}
                icon="times"
                size="xs"
              />
            )}
          </h3>
          <div className="card-body">
            <div className="form-group">
              <label>Email</label>
              <Field
                name="email"
                type="text"
                className={
                  "form-control" +
                  (errors.email && touched.email ? " is-invalid" : "")
                }
              />
              <ErrorMessage
                name="email"
                component="div"
                className="invalid-feedback"
              />
            </div>
            <div className="form-row">
              <div className="form-group col-sm">
                <label>Password</label>
                <Field
                  name="password"
                  type="password"
                  className={
                    "form-control" +
                    (errors.password && touched.password ? " is-invalid" : "")
                  }
                />
                <ErrorMessage
                  name="password"
                  component="div"
                  className="invalid-feedback"
                />
              </div>
              <div className="form-group col-sm">
                <label>Confirm Password</label>
                <Field
                  name="confirmPassword"
                  type="password"
                  className={
                    "form-control" +
                    (errors.confirmPassword && touched.confirmPassword
                      ? " is-invalid"
                      : "")
                  }
                />
                <ErrorMessage
                  name="confirmPassword"
                  component="div"
                  className="invalid-feedback"
                />
              </div>
            </div>
            <div className="form-group form-check">
              <Field
                type="checkbox"
                name="gdpr"
                id="gdpr"
                className={
                  "form-check-input " +
                  (errors.gdpr && touched.gdpr ? " is-invalid" : "")
                }
              />
              <label htmlFor="gdpr" className="form-check-label">
                Yes, I accept{" "}
                <a
                  href="http://www.interreg-danube.eu/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleTerms}
                >
                  Terms &amp; Conditions
                </a>
                , according to the General Data Protection Regulation
              </label>
              <ErrorMessage
                name="gdpr"
                component="div"
                className="invalid-feedback"
              />
            </div>
            <div className="form-group">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary"
              >
                {isSubmitting && (
                  <span className="spinner-border spinner-border-sm mr-1"></span>
                )}
                Register
              </button>
              {isModal ? (
                <button onClick={() => close()} className="btn btn-link">
                  Cancel
                </button>
              ) : (
                <Link to="login" className="btn btn-link">
                  Cancel
                </Link>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

const openRegisterModal = () =>
  modalService.open({
    id: "register",
    children: (
      <Register isModal={true} close={() => modalService.close("register")} />
    ),
  });

export { Register, openRegisterModal };
